import type { HeightLeg } from "../heightProfile";
import type { CircuitLegMetres, CircuitLegs, LegKind } from "./types";

const pointKey = ({ x, y }: { x: number; y: number }): string => `${x.toFixed(2)},${y.toFixed(2)}`;

const segmentKey = (a: { x: number; y: number }, b: { x: number; y: number }): string => {
  const [p, q] = [pointKey(a), pointKey(b)];
  return p < q ? `${p}|${q}` : `${q}|${p}`;
};

const legKey = (leg: HeightLeg): string => `${pointKey(leg.at)}|${leg.fromM}|${leg.toM}`;

const isControl = (role: string | null) => role === "control" || role === "twoWay";

// Read off the drawn cable: before the first switch it is the feed, between two switches the
// Wechselschaltung's run, after the last one the lights — or the Lüfter's own run.
export const circuitLegs: CircuitLegs = ({
  paths,
  profiles,
  roleOf,
  metresPerPoint,
  noTraySurchargePct,
  billedM,
}) => {
  const controlAt = new Set(
    paths
      .filter((path) => isControl(roleOf(path)))
      .map((path) => path.waypoints[path.waypoints.length - 1])
      .filter(Boolean)
      .map(pointKey),
  );
  const measured: Partial<Record<LegKind, number>> = {};
  const vertical: Partial<Record<LegKind, number>> = {};
  const seenSegments = new Set<string>();
  const seenLegs = new Set<string>();

  paths.forEach((path, pathIndex) => {
    const { waypoints } = path;
    const role = roleOf(path);
    const stops = waypoints
      .slice(1, -1)
      .map((point, offset) => (controlAt.has(pointKey(point)) ? offset + 1 : -1))
      .filter((index) => index > 0);
    const kindOf = (segment: number): LegKind => {
      if (!stops.length || segment < stops[0]) return "feed";
      if (segment < stops[stops.length - 1] || isControl(role)) return "traveler";
      return role === "switchedLoad" ? "load" : "switched";
    };

    waypoints.slice(1).forEach((to, index) => {
      const key = segmentKey(waypoints[index], to);
      if (seenSegments.has(key)) return;
      seenSegments.add(key);
      const kind = kindOf(index);
      const metres = Math.hypot(to.x - waypoints[index].x, to.y - waypoints[index].y) * metresPerPoint;
      measured[kind] = (measured[kind] ?? 0) + metres;
    });

    const indexAt = new Map<string, number>();
    waypoints.forEach((point, index) => {
      if (!indexAt.has(pointKey(point))) indexAt.set(pointKey(point), index);
    });
    const last = Math.max(0, waypoints.length - 2);
    (profiles[pathIndex]?.legs ?? []).forEach((leg) => {
      if (leg.kind === "run") return;
      const key = legKey(leg);
      if (seenLegs.has(key)) return;
      seenLegs.add(key);
      const at = indexAt.get(pointKey(leg.at)) ?? 0;
      // The drop into a switch belongs to the cable arriving at it; the climb out, to the cable leaving it.
      const segment =
        leg.reason === "device" ? last : leg.reason === "passDevice" ? Math.max(0, at - 1) : Math.min(at, last);
      const kind = kindOf(segment);
      vertical[kind] = (vertical[kind] ?? 0) + leg.lengthM;
    });
  });

  // Every leg after the switch is off the tray, so it bills at the surcharge; the feed bills what is left.
  const surcharge = 1 + noTraySurchargePct / 100;
  const legs: CircuitLegMetres = {};
  let legsBilled = 0;
  (["traveler", "switched", "load"] as LegKind[]).forEach((kind) => {
    if (measured[kind] === undefined && vertical[kind] === undefined) return;
    const horizontalM = (measured[kind] ?? 0) * surcharge;
    legsBilled += horizontalM;
    legs[kind] = { horizontalM, verticalM: vertical[kind] ?? 0, totalM: horizontalM + (vertical[kind] ?? 0) };
  });
  const feedHorizontal = Math.max(0, billedM - legsBilled);
  legs.feed = { horizontalM: feedHorizontal, verticalM: vertical.feed ?? 0, totalM: feedHorizontal + (vertical.feed ?? 0) };
  return legs;
};
